import type { CSSProperties } from "react";
import React, { useState } from "react";

import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";
dayjs.extend(customParseFormat);
dayjs.extend(utc);
import DatePicker from "react-datepicker";
import styled, { css, type CSSObject } from "styled-components";
import "react-datepicker/dist/react-datepicker.css";

import { colorTheme, gridSpacing } from "@utils";

import { DatePickerWrapper } from "./helpers";

const FormGroup = styled.div<{ $error?: boolean }>`
  display: flex;
  flex-direction: column;
  margin-bottom: ${gridSpacing[4]}px;

  input {
    border-color: ${({ $error }) =>
      $error ? `${colorTheme("danger")} !important` : colorTheme("neutralL4")};
  }
`;

const DatePickerContainer = styled(DatePickerWrapper)<{
  $inputStyle?: CSSProperties;
}>`
  .react-datepicker {
    padding: 8px;
    box-shadow: 0px 8px 32px 0px rgba(0, 0, 0, 0.2);
  }

  .react-datepicker-wrapper {
    width: 100%;
  }

  .react-datepicker__month-container {
    width: 100%;
  }

  .react-datepicker__navigation {
    padding: 14px;
  }

  ${({ $inputStyle }) =>
    $inputStyle &&
    css`
      .date-picker-class {
        ${css($inputStyle as CSSObject)}
      }
    `}
`;

const HelperText = styled.div`
  width: 100%;
  margin-left: 5px;
  font-size: 14px;
`;

const Label = styled.label`
  margin-bottom: 0;
`;

const RequiredSpan = styled.span`
  color: ${colorTheme("danger")};
  font-size: 14px;
  margin-left: 1px;
`;

interface SingleDatePickerProps {
  value: string | null;
  onChange: (value: string | null) => void;
  onBlur?: (value: string | null) => void;
  label?: string;
  error?: boolean | string;
  helperText?: string;
  helperTextStyle?: React.CSSProperties;
  style?: React.CSSProperties;
  labelStyle?: React.CSSProperties;
  inputStyle?: React.CSSProperties;
  required?: boolean;
  requiredStyle?: React.CSSProperties;
  dateTime?: boolean;
  fixedPosition?: boolean;
  testId?: string;
  disabled?: boolean;
}

function SingleDatePicker({
  value,
  onChange,
  onBlur,
  label,
  error,
  helperText,
  helperTextStyle = {},
  style,
  labelStyle = {},
  inputStyle = {},
  required,
  requiredStyle = {},
  dateTime,
  fixedPosition = false,
  testId = "single-date-picker",
  disabled = false,
}: SingleDatePickerProps) {
  const [currentValue, setCurrentValue] = useState<Date | null>(null);

  let selectedValue = null;

  if (value && value !== "Invalid date") {
    const parsed = dateTime
      ? value.includes("Z") ||
        value.includes("+") ||
        value.match(/T\d{2}:\d{2}:\d{2}/)
        ? dayjs(value) // Already formatted
        : dayjs(value, "YYYY-MM-DDTHH:mm", true)
      : dayjs(value, ["YYYY-MM-DDTHH:mm", "YYYY-MM-DD"], true);

    if (parsed.isValid()) {
      selectedValue = parsed.toDate();
    }
  }

  const transformDate = (e: Date | null) => {
    if (e && !isNaN(e.getTime())) {
      const d = dayjs(e);
      if (dateTime) {
        return d.startOf("day").format("YYYY-MM-DDTHH:mm");
      }
      return d.format("YYYY-MM-DD");
    }
    return null;
  };

  return (
    <FormGroup style={style} $error={!!error}>
      <Label
        style={{
          color: error ? colorTheme("danger") : colorTheme("black"),
          ...labelStyle,
        }}
      >
        {label}
        {required && <RequiredSpan style={requiredStyle}>*</RequiredSpan>}
      </Label>
      <div>
        <DatePickerContainer data-testid={testId} $inputStyle={inputStyle}>
          <DatePicker
            fixedHeight
            disabled={disabled}
            popperProps={{
              strategy: fixedPosition ? "fixed" : "absolute",
            }}
            showPopperArrow={false}
            selected={selectedValue}
            placeholderText="Select date"
            onBlur={() => onBlur && onBlur(transformDate(currentValue))}
            onSelect={(date) => onBlur && onBlur(transformDate(date))}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === "") {
                onChange(transformDate(currentValue));
              }
            }}
            onChange={(date: Date | null) => {
              setCurrentValue(date);
              onChange(transformDate(date));
            }}
            className="date-picker-class"
          />
          {error ? (
            <HelperText
              style={{ color: colorTheme("danger"), ...helperTextStyle }}
            >
              {typeof error === "string" ? error : ""}
            </HelperText>
          ) : helperText ? (
            <HelperText style={helperTextStyle}>{helperText}</HelperText>
          ) : null}
        </DatePickerContainer>
      </div>
    </FormGroup>
  );
}

export default SingleDatePicker;
